import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DeviceType, SplitInfo } from 'app/model/Device';
import { LocalizationContext } from 'app/localization/Context';
import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';

import { useRemoveDevicePopup } from '../../DeviceDetail';
import CustomIconBack from 'app/home/home-components/CustomIconBack';

import { ReactComponent as IconDeleteWhite } from 'images/general/delete-white.svg';
import { ReactComponent as IconDeviceSettings } from 'images/setting/settings-gear.svg';
import { ReactComponent as IconDisconnect } from 'images/general/disconnect.svg';

// devices
import { ReactComponent as IconRemoteAc } from 'images/device/symbol/white/remote-ac.svg';
import { ReactComponent as IconRemoteFan } from 'images/device/symbol/white/remote-fan.svg';
// import { ReactComponent as IconRemoteHomeTheater } from 'images/device/symbol/white/remote-home-theater.svg';
import { ReactComponent as IconRemoteProjector } from 'images/device/symbol/white/remote-projector.svg';
// import { ReactComponent as IconRemoteReciever } from 'images/device/symbol/white/remote-reciever.svg';
import { ReactComponent as IconRemoteSoundbar } from 'images/device/symbol/white/remote-soundbar.svg';
import { ReactComponent as IconRemoteTv } from 'images/device/symbol/white/remote-tv.svg';

// buttons
import { ReactComponent as IconReturn } from 'images/device/remote-button/return.svg';
import { ReactComponent as IconBackward } from 'images/device/remote-button/backward.svg';
import { ReactComponent as IconChangeMood } from 'images/device/remote-button/change-mood.svg';
import { ReactComponent as IconDown } from 'images/device/remote-button/down.svg';
import { ReactComponent as IconLeft } from 'images/device/remote-button/left.svg';
import { ReactComponent as IconRight } from 'images/device/remote-button/right.svg';
import { ReactComponent as IconSpeed } from 'images/device/remote-button/speed.svg';
import { ReactComponent as IconForward } from 'images/device/remote-button/forward.svg';
import { ReactComponent as IconLight } from 'images/device/remote-button/light.svg';
import { ReactComponent as IconMenu } from 'images/device/remote-button/menu.svg';
import { ReactComponent as IconMood } from 'images/device/remote-button/mood.svg';
import { ReactComponent as IconMute } from 'images/device/remote-button/mute.svg';
import { ReactComponent as IconNext } from 'images/device/remote-button/next.svg';
import { ReactComponent as IconOk } from 'images/device/remote-button/ok.svg';
import { ReactComponent as IconOnOff } from 'images/device/remote-button/on-off.svg';
import { ReactComponent as IconPause } from 'images/device/remote-button/pause.svg';
import { ReactComponent as IconPlay } from 'images/device/remote-button/play.svg';
import { ReactComponent as IconPrev } from 'images/device/remote-button/prev.svg';
import { ReactComponent as IconRepeat } from 'images/general/repeat.svg';
import { ReactComponent as IconSources } from 'images/device/remote-button/sources.svg';
import { ReactComponent as IconStop } from 'images/device/remote-button/stop.svg';
import { ReactComponent as IconSwingH } from 'images/device/remote-button/swing-h.svg';
import { ReactComponent as IconSwingV } from 'images/device/remote-button/swing-v.svg';
import { ReactComponent as IconTimer } from 'images/device/remote-button/timer.svg';
import { ReactComponent as IconUp } from 'images/device/remote-button/up.svg';
import { ReactComponent as IconVolDown } from 'images/device/remote-button/vol-down.svg';
import { ReactComponent as IconVolUp } from 'images/device/remote-button/vol-up.svg';

// Split Monitor
import { ReactComponent as IconMonitorModeAuto } from 'images/device/split-monitor/mode-auto.svg';
import { ReactComponent as IconMonitorModeCooling } from 'images/device/split-monitor/mode-cooling.svg';
import { ReactComponent as IconMonitorModeDry } from 'images/device/split-monitor/mode-dry.svg';
import { ReactComponent as IconMonitorModeFan } from 'images/device/split-monitor/mode-fan.svg';
import { ReactComponent as IconMonitorModeHeating } from 'images/device/split-monitor/mode-heating.svg';
import { ReactComponent as IconMonitorSpeedAuto } from 'images/device/split-monitor/speed-auto.svg';
import { ReactComponent as IconMonitorSpeedFast } from 'images/device/split-monitor/speed-fast.svg';
import { ReactComponent as IconMonitorSpeedMedium } from 'images/device/split-monitor/speed-medium.svg';
import { ReactComponent as IconMonitorSpeedSlow } from 'images/device/split-monitor/speed-slow.svg';

import { ReactComponent as IconMonitorModeAutoDark } from 'images/device/split-monitor/mode-auto-dark.svg';
import { ReactComponent as IconMonitorModeCoolingDark } from 'images/device/split-monitor/mode-cooling-dark.svg';
import { ReactComponent as IconMonitorModeDryDark } from 'images/device/split-monitor/mode-dry-dark.svg';
import { ReactComponent as IconMonitorModeFanDark } from 'images/device/split-monitor/mode-fan-dark.svg';
import { ReactComponent as IconMonitorModeHeatingDark } from 'images/device/split-monitor/mode-heating-dark.svg';
import { ReactComponent as IconMonitorSpeedAutoDark } from 'images/device/split-monitor/speed-auto-dark.svg';
import { ReactComponent as IconMonitorSpeedFastDark } from 'images/device/split-monitor/speed-fast-dark.svg';
import { ReactComponent as IconMonitorSpeedMediumDark } from 'images/device/split-monitor/speed-medium-dark.svg';
import { ReactComponent as IconMonitorSpeedSlowDark } from 'images/device/split-monitor/speed-slow-dark.svg';

import './RemoteControl.scss';


export function RemoteControl({ negotiatedProtocol, currentUser, devices }) {
    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);

    const parentDeviceMac = p.state.parentDeviceMac;
    const subDeviceMac = p.state.subDeviceMac;
    const dismiss = p.dismiss;

    const l = useContext(LocalizationContext);
    const s = l.strings;

    const [parentDevice, subDevice] = useMemo(() => {
        let parentDevice = devices.find(({ mac }) => mac === parentDeviceMac);
        let subDevice = parentDevice.sub_devices.find(({ mac }) => mac === subDeviceMac);
        return [parentDevice, subDevice];
    }, [devices, parentDeviceMac, subDeviceMac]);

    const active = parentDevice.active;

    const [deviceCache, setDeviceCache] = useState(subDevice);
    useEffect(() => {
        if (subDevice !== undefined) {
            setDeviceCache(subDevice);
        }
    }, [subDevice]);

    const sendCommandH = useCallback((func) => {
        return () => {
            deviceCache.changeState({ 'function': func });
        }
    }, [deviceCache]);

    const dcPlane = useMemo(() => {
        if (active) {
            return null;
        } else {
            return <div className="device-detail-dc-panel"
                dir={l.isRTL ? "rtl" : "ltr"}
                lang={l.language}>
                <div className="ir-emitter-icon" />
                <div>
                    <div />
                    <div className="label connection-label">{s.deviceDisonnected}</div>
                    <IconDisconnect className="icon" />
                    <div />
                </div>
            </div>;
        }
    }, [active, s.deviceDisonnected, l.isRTL, l.language]);

    const deviceSpecificController = useMemo(() => {
        if (deviceCache) {
            switch (deviceCache.type) {
                case DeviceType.tvRemote:
                    return <TV device={deviceCache} dcPlane={dcPlane} sendCommandH={sendCommandH} />
                case DeviceType.receiverRemote:
                    return null;
                case DeviceType.fanRemote:
                    return <Fan device={deviceCache} dcPlane={dcPlane} />
                case DeviceType.splitRemote:
                    return <Split device={deviceCache} dcPlane={dcPlane} sendCommandH={sendCommandH} />
                case DeviceType.soundBarRemote:
                    return <SoundBar device={deviceCache} dcPlane={dcPlane} />
                case DeviceType.projectorRemote:
                    return <Projector device={deviceCache} dcPlane={dcPlane} />
                case DeviceType.homeTheatreRemote:
                    return null;
                default:
                    return null;
            }
        }
    }, [deviceCache, sendCommandH, dcPlane]);

    const [openRemovePopup, removeDevicePopup] = useRemoveDevicePopup(deviceCache, dismiss);

    // const [deviceSettingPageActive, setDeviceSettingPageActive] = useState(false);
    // const setChooseNameActive = () => setDeviceSettingPageActive(true);

    return (
        <div className="remote-control-page">
            <div className="bg-blank" />
            {active ?
                <div className="bg-secondary-color" /> :
                <div className="bg-disconnect-color" />
            }

            <div className="remote-control-page-content">
                <div className="nav-button" onClick={dismiss} />

                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                    <div />

                    {
                        (negotiatedProtocol.hasDeviceSetting) && (currentUser.permissions.setting) ? (
                            <Fragment>
                                <div className="button-border" onClick={() => {hs.push("/devices/detail/settings", { mac: deviceCache.mac })}}>
                                    <div className="label">{s.deviceSettings.button}</div>
                                    <IconDeviceSettings className="icon"  />
                                </div>
                                <IconDeleteWhite className="remove-icon" onClick={openRemovePopup} />
                            </Fragment>
                        ) : null
                    }
                </div>

                {deviceSpecificController}

                {removeDevicePopup}
            </div>
        </div>
    )
}

export function Split({ device, sendCommandH, dcPlane, style, lightAndMinimal }) {
    let modeEnable = true;
    let setpointUpEnable = true;
    let setpointDownEnable = true;
    let fanEnable = true;
    let showSetpoint = true;

    if ((device !== undefined) && (device["config_per_mode"] !== undefined)) {
        const splitConf = device["config_per_mode"][String(device["mode"])];
        let isOn = device.is_on === true; // maybe "=== true" is not necessary
        modeEnable = isOn && Object.keys(device["config_per_mode"]).length > 0;
        showSetpoint = isOn && (splitConf["set_point_enable"]);
        setpointUpEnable = isOn && (splitConf["set_point_enable"]) && (splitConf["max_set_point"] > device["set_point"]);
        setpointDownEnable = isOn && (splitConf["set_point_enable"]) && (splitConf["min_set_point"] < device["set_point"]);
        fanEnable = isOn && splitConf["fan_enable"];
    }

    let setpoint = "----";
    let modeIcon = IconMonitorModeCooling;
    let fanIcon = undefined;
    let fanBars = 0;
    if (lightAndMinimal) {
        if (device !== undefined) {
            if (showSetpoint) {
                if (device["set_point"] !== undefined) {
                    setpoint = device["set_point"];
                }
            }
            switch (device["mode"]) {
                case SplitInfo.mode.heat:
                    modeIcon = IconMonitorModeHeatingDark;
                    break;
                case SplitInfo.mode.cool:
                    modeIcon = IconMonitorModeCoolingDark;
                    break;
                case SplitInfo.mode.dry:
                    modeIcon = IconMonitorModeDryDark;
                    break;
                case SplitInfo.mode.auto:
                    modeIcon = IconMonitorModeAutoDark;
                    break;
                case SplitInfo.mode.fanOnly:
                    modeIcon = IconMonitorModeFanDark;
                    break;
                default:
                    break;
            }
            if (fanEnable) {
                switch (device["fan"]) {
                    case SplitInfo.fan.auto:
                        fanIcon = IconMonitorSpeedAutoDark;
                        fanBars = 0;
                        break;
                    case SplitInfo.fan.x1:
                        fanIcon = IconMonitorSpeedSlowDark;
                        fanBars = 2;
                        break;
                    case SplitInfo.fan.x2:
                        fanIcon = IconMonitorSpeedMediumDark;
                        fanBars = 3;
                        break;
                    case SplitInfo.fan.x3:
                        fanIcon = IconMonitorSpeedFastDark;
                        fanBars = 5;
                        break;
                    default:
                        break;
                }
            }
        }
    } else {
        if (device !== undefined) {
            if (showSetpoint) {
                if (device["set_point"] !== undefined) {
                    setpoint = device["set_point"];
                }
            }
            switch (device["mode"]) {
                case SplitInfo.mode.heat:
                    modeIcon = IconMonitorModeHeating;
                    break;
                case SplitInfo.mode.cool:
                    modeIcon = IconMonitorModeCooling;
                    break;
                case SplitInfo.mode.dry:
                    modeIcon = IconMonitorModeDry;
                    break;
                case SplitInfo.mode.auto:
                    modeIcon = IconMonitorModeAuto;
                    break;
                case SplitInfo.mode.fanOnly:
                    modeIcon = IconMonitorModeFan;
                    break;
                default:
                    break;
            }
            if (fanEnable) {
                switch (device["fan"]) {
                    case SplitInfo.fan.auto:
                        fanIcon = IconMonitorSpeedAuto;
                        fanBars = 0;
                        break;
                    case SplitInfo.fan.x1:
                        fanIcon = IconMonitorSpeedSlow;
                        fanBars = 2;
                        break;
                    case SplitInfo.fan.x2:
                        fanIcon = IconMonitorSpeedMedium;
                        fanBars = 3;
                        break;
                    case SplitInfo.fan.x3:
                        fanIcon = IconMonitorSpeedFast;
                        fanBars = 5;
                        break;
                    default:
                        break;
                }
            }
        }
    }


    return (
        <div className={"control-box fan" + (lightAndMinimal ? " light-and-minimal" : "")} dir="ltr" style={style}>
            {
                lightAndMinimal ? null : <div className="ir-emitter-icon" />
            }
            {/* <div className="powered-by-hinava">Powered By Hinava</div> */}

            {device !== undefined ?
                <Fragment>
                    {
                        lightAndMinimal ? null : (
                            <div className="item header r1 c1-4">
                                <div className="name">{device.name}</div>
                                <IconRemoteAc className="icon" />
                            </div>
                        )
                    }

                    <div className="item monitor-container r2 c1-4" style={{ marginBottom: 32 }}>
                        <div className={device.is_on ? "monitor" : "monitor off"}>
                            <div className="row-1">
                                <div className="temp">{setpoint}</div>
                                {React.createElement(modeIcon, { className: "mode" })}
                            </div>
                            {fanIcon ? <div className="row-2">
                                {React.createElement(fanIcon, { className: "fan-icon" })}
                                <div className="fan-bars">
                                    {
                                        fanBars === 0 ? <div className="auto">Auto</div> :
                                            Array.from(Array(fanBars).keys()).map(
                                                (size) =>
                                                    <div className="bar" style={{ height: 12 + (size) * (20 / 6) }} key={`split-bar-size-key-${size}`} />
                                            )
                                    }
                                </div>
                            </div> : null}
                        </div>
                    </div>
                </Fragment>
                : null}

            <SquareButton icon={IconOnOff} title={"ON/OFF"} onClick={sendCommandH(0)} className="r3 c2 b36" />

            <SquareButton icon={IconSpeed} title={"Speed"} onClick={fanEnable ? sendCommandH(6) : () => { }} className={fanEnable ? "r4 c1 b36" : "r4 c1 b36 disabled"} />
            {/* <SquareButton icon={IconSwingV} title={"Swing"} onClick={sendCommandH(4)} className="r5 c1 b36 disabled" /> */}
            <SquareButton icon={IconSwingV} title={"Swing"} onClick={() => { }} className="r5 c1 b36 disabled" />

            <VerticalButton
                iconTop={IconUp} iconBottom={IconDown}
                onClickTop={setpointUpEnable ? sendCommandH(1) : () => { }}
                onClickBottom={setpointDownEnable ? sendCommandH(2) : () => { }}
                topDisabled={!setpointUpEnable} bottomDisabled={!setpointDownEnable}
                title={"Temp"} className="r4-6 c2 b36" />

            <SquareButton icon={IconMood} title={"Mode"} onClick={modeEnable ? sendCommandH(5) : () => { }} className={modeEnable ? "r4 c3 b36" : "r4 c3 b36 disabled"} />
            {/* <SquareButton icon={IconSwingH} title={"Swing"} onClick={sendCommandH(3)} className="r5 c3 b36 disabled" /> */}
            <SquareButton icon={IconSwingH} title={"Swing"} onClick={() => { }} className="r5 c3 b36 disabled" />

            {dcPlane}
        </div>
    )
}

export function TV({ device, sendCommandH, dcPlane, style }) {
    return (
        <div className="control-box fan" dir="ltr" style={style}>
            <div className="ir-emitter-icon" />
            {/* <div className="powered-by-hinava">Powered By Hinava</div> */}

            {device !== undefined ?
                <div className="item header r1 c1-4">
                    <div className="name">{device.name}</div>
                    <IconRemoteTv className="icon" />
                </div> : null}

            <SquareButton icon={IconOnOff} title={"ON/OFF"} onClick={sendCommandH(0)} className="r2 c2 b36" />

            <SquareButton icon={IconReturn} title={"Return"} onClick={sendCommandH(8)} className="r3 c1" />
            <SquareButton icon={IconMenu} title={"Menu"} onClick={sendCommandH(7)} className="r3 c3" />

            <FourWaysButton
                iconTop={IconUp} iconBottom={IconDown} iconLeft={IconLeft} iconRight={IconRight}
                onClickTop={sendCommandH(11)} onClickBottom={sendCommandH(12)}
                onClickLeft={sendCommandH(9)} onClickRight={sendCommandH(10)}
                onClickCenter={sendCommandH(13)}
                iconCenter={IconOk} className="r4 c1-4 b36" />

            <VerticalButton
                iconTop={IconVolUp} iconBottom={IconVolDown}
                onClickTop={sendCommandH(3)} onClickBottom={sendCommandH(4)}
                title={"Vol"} className="r5-7 c1 b36" />
            <SquareButton icon={IconMute} title={"Mute"} onClick={sendCommandH(6)} className="r5 c2 b36" />
            <SquareButton icon={IconSources} title={"Source"} onClick={sendCommandH(5)} className="r6 c2 b36" />
            <VerticalButton
                iconTop={IconUp} iconBottom={IconDown}
                onClickTop={sendCommandH(1)} onClickBottom={sendCommandH(2)}
                title={"Page"} className="r5-7 c3 b36" />

            {dcPlane}
        </div>
    )
}

export function SoundBar({ device, dcPlane, style }) {
    return (
        <div className="control-box fan" dir="ltr" style={style}>
            <div className="ir-emitter-icon" />
            {/* <div className="powered-by-hinava">Powered By Hinava</div> */}

            {device !== undefined ?
                <div className="item header r1 c1-4">
                    <div className="name">{device.name}</div>
                    <IconRemoteSoundbar className="icon" />
                </div> : null}

            <SquareButton icon={IconOnOff} title={"ON/OFF"} className="r2 c1 b24" />
            <SquareButton title={"TUNER"} className="r2 c3 b24" />

            <SquareButton title={"DVD"} className="r3 c1 b24" />
            <SquareButton title={"PORT"} className="r3 c2 b24" />
            <SquareButton title={"AUX"} className="r3 c3 b24" />

            <SquareButton icon={IconBackward} title={"Backw"} className="r4 c1 b24" />
            <SquareButton icon={IconRepeat} title={"Repeat"} className="r4 c2 b24" />
            <SquareButton icon={IconForward} title={"Forw"} className="r4 c3 b24" />

            <SquareButton icon={IconPrev} title={"Prev"} className="r5 c1 b24" />
            <SquareButton icon={IconPause} title={"Pause"} className="r5 c2 b24" />
            <SquareButton icon={IconNext} title={"Next"} className="r5 c3 b24" />


            <VerticalButton iconTop={IconVolUp} iconBottom={IconVolDown} title={"Vol"} className="r6-8 c1 b36" />
            <SquareButton icon={IconPlay} title={"Play"} className="r6 c2 b36" />
            <SquareButton icon={IconStop} title={"Stop"} className="r7 c2 b36" />
            <VerticalButton iconTop={IconUp} iconBottom={IconDown} title={"Page"} className="r6-8 c3 b36" />

            {dcPlane}
        </div>
    )
}

export function Projector({ device, dcPlane, style }) {
    return (
        <div className="control-box fan" dir="ltr" style={style}>
            <div className="ir-emitter-icon" />
            {/* <div className="powered-by-hinava">Powered By Hinava</div> */}

            {device !== undefined ?
                <div className="item header r1 c1-4">
                    <div className="name">{device.name}</div>
                    <IconRemoteProjector className="icon" />
                </div> : null}

            <SquareButton icon={IconOnOff} title={"ON/OFF"} className="r2 c1 b24" />
            <SquareButton title={"LAN"} className="r2 c3 b24" />

            <SquareButton title={"HDMI 1"} className="r3 c1 b24" />
            <SquareButton title={"HDMI 2"} className="r3 c2 b24" />
            <SquareButton title={"VIDEO"} className="r3 c3 b24" />

            <FourWaysButton iconTop={IconUp} iconBottom={IconDown} iconLeft={IconLeft} iconRight={IconRight} iconCenter={IconOk} className="r4 c1-4 b24" />

            <VerticalButton iconTop={IconVolUp} iconBottom={IconVolDown} title={"Vol"} className="r5-7 c1 b36" />
            <SquareButton icon={IconMenu} title={"Menu"} className="r5 c3 b36" />
            <SquareButton title={"Esc"} className="r6 c3 b36" />

            {dcPlane}
        </div>
    )
}

export function Fan({ device, dcPlane, style }) {
    return (
        <div className="control-box fan" dir="ltr" style={style}>
            <div className="ir-emitter-icon" />
            {/* <div className="powered-by-hinava">Powered By Hinava</div> */}

            {device !== undefined ?
                <div className="item header r1 c1-4">
                    <div className="name">{device.name}</div>
                    <IconRemoteFan className="icon" />
                </div> : null}

            <SquareButton icon={IconOnOff} title={"ON/OFF"} className="r2 c2 b36" />

            <SquareButton icon={IconSpeed} title={"Speed"} className="r3 c1 b36" />
            <SquareButton icon={IconLight} title={"Light"} className="r3 c2 b36" />
            <SquareButton icon={IconChangeMood} title={"Mode"} className="r3 c3 b36" />

            <SquareButton icon={IconTimer} title={"Timer"} className="r4 c1 b36" />
            <SquareButton icon={IconSwingH} title={"Swing"} className="r4 c3 b36" />

            {dcPlane}
        </div>
    )
}

function SquareButton({ className, icon, title, onClick }) {
    return (
        <div className={"item button square " + className} onClick={onClick}>
            {icon ? React.createElement(icon, { className: "icon" }) : null}
            <div className="title">{title}</div>
        </div>
    )
}

function VerticalButton({ className, iconTop, iconBottom, topDisabled, bottomDisabled, title, onClickTop, onClickBottom }) {
    return (
        <div className={"item vertical " + className}>
            <div className={topDisabled ? "button top disabled" : "button top"} onClick={onClickTop}>
                {React.createElement(iconTop, { className: "icon" })}
            </div>
            <div className={(topDisabled && bottomDisabled) ? "center disabled" : "center"}>
                <div className="title">{title}</div>
            </div>
            <div className={bottomDisabled ? "button bottom disabled" : "button bottom"} onClick={onClickBottom}>
                {React.createElement(iconBottom, { className: "icon" })}
            </div>
        </div>
    )
}

function FourWaysButton({
    className, iconTop, iconBottom, iconLeft, iconRight, iconCenter,
    onClickTop, onClickBottom, onClickLeft, onClickRight, onClickCenter
}) {

    return (
        <div className={"item four-ways " + className}>
            <div className="button top" onClick={onClickTop}>
                {React.createElement(iconTop, { className: "icon" })}
            </div>
            <div className="button center" onClick={onClickCenter}>
                {React.createElement(iconCenter, { className: "icon" })}
            </div>
            <div className="button left" onClick={onClickLeft}>
                {React.createElement(iconLeft, { className: "icon" })}
            </div>
            <div className="button right" onClick={onClickRight}>
                {React.createElement(iconRight, { className: "icon" })}
            </div>
            <div className="button bottom" onClick={onClickBottom}>
                {React.createElement(iconBottom, { className: "icon" })}
            </div>
        </div>
    )
}
